import type { Citation } from "../components/review/citation-context";

export interface HighlightSegment {
  text: string;
  citation: Citation | null;
}

/**
 * Splits `text` into segments, marking substrings that match any citation quote.
 * Case-insensitive. First match wins when quotes overlap.
 *
 * Also handles the reverse case for short text (skill chips, language names):
 * if no quote is found as a substring of `text`, but `text` appears inside a
 * citation quote, the entire text is highlighted with that citation.
 */
export function highlightText(
  text: string,
  citations: Citation[],
): HighlightSegment[] {
  if (!text || citations.length === 0) {
    return [{ text, citation: null }];
  }

  interface Match {
    start: number;
    end: number;
    citation: Citation;
  }

  const matches: Match[] = [];
  const lower = text.toLowerCase();

  for (const cit of citations) {
    let q = cit.quote.toLowerCase();
    if (!q) continue;
    // Strip trailing ellipsis that the LLM adds when truncating quotes
    q = q.replace(/\s*[…\.]{2,}\s*$/, "").trim();
    if (!q) continue;
    let pos = 0;
    while (pos < lower.length) {
      const idx = lower.indexOf(q, pos);
      if (idx === -1) break;
      matches.push({ start: idx, end: idx + q.length, citation: cit });
      pos = idx + q.length;
    }
  }

  if (matches.length === 0) {
    const trimmed = lower.trim();
    if (trimmed.length >= 2) {
      for (const cit of citations) {
        if (cit.quote.toLowerCase().includes(trimmed)) {
          return [{ text, citation: cit }];
        }
      }
    }
    return [{ text, citation: null }];
  }

  matches.sort((a, b) => a.start - b.start || b.end - a.end);

  const merged: Match[] = [];
  for (const m of matches) {
    if (merged.length > 0 && m.start < merged[merged.length - 1].end) continue;
    merged.push(m);
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;

  for (const m of merged) {
    if (m.start > cursor) {
      segments.push({ text: text.slice(cursor, m.start), citation: null });
    }
    segments.push({
      text: text.slice(m.start, m.end),
      citation: m.citation,
    });
    cursor = m.end;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), citation: null });
  }

  return segments;
}
